<template>
	<div>
		<div
			v-if="tile?.numberOfRevealedTiles !== tile?.numberOfPoiTiles"
			class="poi-detail-tile-counter"
		>
			{{ translate("ui.explore_monument") }} ({{
				tile?.numberOfRevealedTiles
			}}/{{ tile?.numberOfPoiTiles }})
		</div>
		<div v-else class="poi-detail">
			<div class="poi-detail__content">
				<BidirectionalTextLabel
					:text="translate('ui.rewards')"
					:theme="LabelThemes.GREEN"
					class="rewards-label"
				/>
				<div class="poi-detail-reward">
					<Tooltip
						v-for="(amount, currency, index) in tile?.getRewards()"
						:key="currency"
						:placement="Placements.TOP"
					>
						<template #element>
							<AppPrice
								:key="index"
								:amount="amount || 0"
								:currency="currency"
								:resourceCheck="false"
								:iconSize="16"
							/>
						</template>
						<template #tooltip>
							<span>
								{{
									translate(
										`resource.${resourceTypeToCurrencyType(
											currency,
										)}`,
									)
								}}
							</span>
						</template>
					</Tooltip>
				</div>
				<div class="reward-text" v-for="text in rewardText" :key="text">
					{{ text }}
				</div>
				<div class="poi-detail-reward">
					<Tooltip
						v-for="(resource, index) in tile?.storage"
						:key="resource[0]"
						:placement="Placements.TOP"
					>
						<template #element>
							<AppPrice
								:key="index"
								:amount="resource[1]"
								:currency="resource[0]"
								:resourceCheck="false"
								:iconSize="16"
							/>
						</template>
						<template #tooltip>
							<span>
								{{
									translate(
										`resource.${resourceTypeToCurrencyType(
											resource[0],
										)}`,
									)
								}}
							</span>
						</template>
					</Tooltip>
				</div>
			</div>
			<RectangleBoxContainer />
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppPrice from "@/components/AppPrice.vue";
import BidirectionalTextLabel from "@/components/BidirectionalTextLabel.vue";
import POIPopup from "@/components/POI/POIPopup.vue";
import RectangleBoxContainer from "@/components/SVG/RectangleBoxContainer.vue";
import Tooltip from "@/components/Tooltip.vue";
import { FX1AudioNames } from "@/game/entities/config/audio/audioConfig";
import type AbstractPoiTileEntity from "@/game/entities/poi/AbstractPoiTileEntity";
import SoundService from "@/game/SoundService";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import { LabelThemes, Placements, SkinTypes } from "@/types/ui";
import { resourceTypeToCurrencyType } from "@/utils/formatPrice";
import { replacePlaceholder } from "@/utils/replacePlaceholder";
import { canTranslate, translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, markRaw, onMounted } from "vue";

interface Props {
	tile?: AbstractPoiTileEntity;
}

const { tile = null } = defineProps<Props>();

const { poiJustRevealed } = storeToRefs(useRuntimeStore());
const popupStore = usePopupStore();

const rewardText = computed((): string[] => {
	if (!tile) return [];
	const text = [] as string[];
	for (let i = 0; i < tile.rewardText.length; i++) {
		const rewardText = tile.rewardText[i];
		text[i] = canTranslate(rewardText) ? translate(rewardText) : "";
		Object.entries(tile?.rewardTextReplace[i] ?? {}).forEach(
			([key, value]: [string, string]): void => {
				const textTranslated = canTranslate(value)
					? translate(value)
					: value;
				text[i] = replacePlaceholder(text[i], key, textTranslated);
			},
		);
	}
	return text;
});

onMounted(() => {
	if (tile?.isFullyExplored && poiJustRevealed.value) {
		SoundService.getInstance().playFX1(FX1AudioNames.wonder_discovery_sfx);

		popupStore.showPopup({
			id: PopupDictionary.MONUMENT_REVEALED,
			type: PopupType.INFO,
			heading: translate("ui.monument_revealed"),
			skin: SkinTypes.DARK,
			maxHeight: 52,
			width: 26.875,
			percentageWidth: "98%",
			isPaddingless: true,
			isCentered: false,
			imgSrc: `POI/${tile.poiPlagueFrameName.substring(0, tile.poiPlagueFrameName.length - 1)}.avif`,
			component: markRaw(POIPopup),
			componentProps: {
				isClickable: true,
				tile: tile,
			},
		});
	}
});
</script>

<style lang="scss" scoped>
.poi-detail-tile-counter {
	@include flex-row-center;
}
.poi-detail {
	width: 22.313rem;
	@include flex-row-center;
	position: relative;
	margin: 0 auto;
	margin-top: 1rem;
	box-shadow: inset 0 0 2.375rem 0rem rgba($color: #2bad50, $alpha: 0.7);
	background-image: linear-gradient(to top, rgb(37, 38, 42), rgb(66, 67, 74));

	&::before {
		position: absolute;
		top: 0;
		left: 0;
		opacity: 0.6;
		mix-blend-mode: color-burn;
		background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
			repeat center;
		background-size: 40%;
		width: 100%;
		height: 100%;
		pointer-events: none;
		content: "";
	}

	&__content {
		@include flex-col-center;
		padding: 2rem 0.5rem;
		width: 100%;
		height: 100%;
	}

	.rewards-label {
		position: absolute;
		top: -1rem;
		z-index: 1;
		width: auto;
		height: 2rem;
	}

	:deep(.bidirectional-text-label) {
		filter: drop-shadow(0 0 0.25rem rgba(0, 0, 0, 0.6));
	}

	:deep(.price__value) {
		color: #f2dcb5 !important;
	}

	.poi-detail-reward {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 0.625rem;

		.price {
			min-width: 34%; // max 2 na riadok
		}
	}
	.reward-text {
		font-size: 0.875rem;
		text-align: center;
	}
}
</style>
